// src/school/pages/Students.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import "./students.css";

export default function Students() {

  const navigate = useNavigate();

  const schoolData = JSON.parse(
    localStorage.getItem("schoolAdmin")
  );

  const schoolId = schoolData?.school?.id;

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // =========================================
  // LOAD STUDENTS
  // =========================================
  const loadStudents = async () => {

    try {
      setIsLoading(true);
      setLoadError("");

      const res = await API.get(
        "/school-admin/students/",
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        }
      );

      setStudents(res.data.students || []);
      setFilteredStudents(res.data.students || []);

    } catch (error) {

      console.error(
        "Error loading students:",
        error
      );
      setLoadError("Unable to load students right now.");
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================
  // LOAD CLASSES
  // =========================================
  const loadClasses = async () => {

    try {

      const res = await API.get(
        "/school-admin/classes/",
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        }
      );

      setClasses(res.data.classes || []);

    } catch (e) {

      console.log(
        "Error loading classes",
        e
      );
    }
  };

  // =========================================
  // LOAD SECTIONS
  // =========================================
  const loadSections = async (classId) => {

    if (!classId) {
      setSections([]);
      return;
    }

    try {

      const res = await API.get(
        `/school-admin/sections/${classId}/`,
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        }
      );

      setSections(res.data.sections || []);

    } catch (e) {

      console.log(
        "Error loading sections",
        e
      );
    }
  };

  // =========================================
  // FILTER
  // =========================================
  const filterStudents = (cls, sec, query = searchTerm) => {

    let data = students;

    if (cls) {
      data = data.filter(
        (s) => s.class_id === Number(cls)
      );
    }

    if (sec) {
      data = data.filter(
        (s) => s.section_id === Number(sec)
      );
    }

    const normalizedQuery = query.trim().toLowerCase();

    if (normalizedQuery) {
      data = data.filter((s) =>
        [
          s.name,
          s.student_id,
          s.roll_number,
          s.parent_name,
          s.parent_phone,
          s.class,
          s.section,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(normalizedQuery)
          )
      );
    }

    setFilteredStudents(data);
  };

  // =========================================
  // DELETE STUDENT
  // =========================================
  const deleteStudent = async (id) => {

    const confirmDelete = window.confirm(
      "Delete this student permanently?"
    );

    if (!confirmDelete) return;

    try {

      await API.delete(
        `/school-admin/students/delete/${id}/`,
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        }
      );

      alert(
        "Student deleted successfully"
      );

      loadStudents();

    } catch (error) {

      console.error(error);

      alert(
        "Failed to delete student"
      );
    }
  };

  // =========================================
  // ARCHIVE STUDENT
  // =========================================
  const archiveStudent = async (id) => {

    const confirmArchive = window.confirm(
      "Archive this student?"
    );

    if (!confirmArchive) return;

    try {

      await API.post(
        `/school-admin/students/archive/${id}/`,
        {},
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        }
      );

      alert(
        "Student archived successfully"
      );

      loadStudents();

    } catch (error) {

      console.error(error);

      alert(
        "Failed to archive student"
      );
    }
  };

  // =========================================
  // CSV EXPORT
  // =========================================
  const downloadCSV = () => {

    if (filteredStudents.length === 0) {

      alert("No students to export");

      return;
    }

    const headers = [
      "Name",
      "Student ID",
      "Class",
      "Section",
      "Roll",
      "Gender",
      "Parent Name",
      "Parent Phone",
    ];

    const rows = filteredStudents.map(
      (s) => [
        s.name,
        s.student_id,
        s.class,
        s.section,
        s.roll_number,
        s.gender,
        s.parent_name,
        s.parent_phone,
      ]
    );

    let csvContent =
      "data:text/csv;charset=utf-8,";

    csvContent += headers.join(",") + "\n";

    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const encodedUri =
      encodeURI(csvContent);

    const link =
      document.createElement("a");

    link.setAttribute("href", encodedUri);

    link.setAttribute(
      "download",
      "students_export.csv"
    );

    document.body.appendChild(link);

    link.click();

    link.remove();
  };

  // =========================================
  // DETAILS
  // =========================================
  const goToDetails = (id) => {

    navigate(
      `/school-admin/students/details/${id}`
    );
  };

  // =========================================
  // EFFECTS
  // =========================================
  useEffect(() => {

    loadStudents();
    loadClasses();

  }, []);

  useEffect(() => {

    loadSections(selectedClass);

    filterStudents(
      selectedClass,
      selectedSection
    );

  }, [selectedClass]);

  useEffect(() => {

    filterStudents(
      selectedClass,
      selectedSection,
      searchTerm
    );

  }, [selectedSection, searchTerm, students]);

  const totalClasses = new Set(
    students.map((s) => s.class).filter(Boolean)
  ).size;

  const parentsLinked = students.filter(
    (s) => s.parent_phone || s.parent_name
  ).length;

  const activeFilters = [
    selectedClass && "Class",
    selectedSection && "Section",
    searchTerm && "Search",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSearchTerm("");
  };

  // =========================================
  // UI
  // =========================================
  return (



      <div className="dhs_students_page">

        {/* ================================= */}
        {/* HEADER */}
        {/* ================================= */}

        <div className="dhs_students_hero">

          <div>

            <span className="dhs_students_eyebrow">
              Students
            </span>

            <h2 className="dhs_students_title">
              Student Management
            </h2>

            <p className="dhs_students_subtitle">
              Showing {filteredStudents.length} of {students.length} student records
            </p>

          </div>

          <div className="dhs_students_header_actions">

            <button
              className="dhs_students_export_btn"
              onClick={downloadCSV}
              disabled={filteredStudents.length === 0}
            >
              Export CSV
            </button>

            <button
              className="dhs_students_add_btn"
              onClick={() =>
                navigate(
                  "/school-admin/students/add"
                )
              }
            >
              Add Student
            </button>

          </div>

        </div>

        <div className="dhs_students_metrics">
          <div className="dhs_students_metric_card">
            <span>Total Students</span>
            <strong>{students.length}</strong>
          </div>

          <div className="dhs_students_metric_card">
            <span>Visible Records</span>
            <strong>{filteredStudents.length}</strong>
          </div>

          <div className="dhs_students_metric_card">
            <span>Classes</span>
            <strong>{totalClasses}</strong>
          </div>

          <div className="dhs_students_metric_card">
            <span>Parent Contacts</span>
            <strong>{parentsLinked}</strong>
          </div>
        </div>

        <div className="dhs_students_filter_wrapper">

          <div className="dhs_students_search_box">
            <label>
              Search
            </label>

            <input
              value={searchTerm}
              placeholder="Name, ID, roll, parent, phone"
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />
          </div>

          <div className="dhs_students_filter_box">

            <label>
              Class
            </label>

            <select
              value={selectedClass}
              onChange={(e) => {

                setSelectedClass(
                  e.target.value
                );

                setSelectedSection("");

              }}
            >

              <option value="">
                All Classes
              </option>

              {classes.map((c) => (

                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>

              ))}

            </select>

          </div>

          <div className="dhs_students_filter_box">

            <label>
              Section
            </label>

            <select
              value={selectedSection}
              disabled={sections.length === 0}
              onChange={(e) =>
                setSelectedSection(
                  e.target.value
                )
              }
            >

              <option value="">
                All Sections
              </option>

              {sections.map((s) => (

                <option
                  key={s.id}
                  value={s.id}
                >
                  {s.name}
                </option>

              ))}

            </select>

          </div>

          <button
            className="dhs_students_clear_btn"
            onClick={clearFilters}
            disabled={activeFilters === 0}
          >
            Clear
          </button>

        </div>

        {/* ================================= */}
        {/* TABLE */}
        {/* ================================= */}

        <div className="dhs_students_table_card">

          <div className="dhs_students_table_top">
            <div>
              <h3>Student Directory</h3>
              <p>{activeFilters} active filter{activeFilters === 1 ? "" : "s"}</p>
            </div>
          </div>

          <table className="dhs_students_table">

            <thead>

              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Student ID</th>
                <th>Class</th>
                <th>Section</th>
                <th>Roll</th>
                <th>Gender</th>
                <th>Parent</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>

            </thead>

            <tbody>

              {isLoading ? (

                <tr>
                  <td
                    colSpan="10"
                    className="dhs_students_no_data"
                  >
                    <div className="dhs_students_state">
                      <span className="dhs_students_loader"></span>
                      <strong>Loading students</strong>
                      <p>Preparing the student directory.</p>
                    </div>
                  </td>
                </tr>

              ) : loadError ? (

                <tr>
                  <td
                    colSpan="10"
                    className="dhs_students_no_data"
                  >
                    <div className="dhs_students_state dhs_students_state_error">
                      <strong>{loadError}</strong>
                      <p>Refresh the list after checking the connection.</p>
                      <button onClick={loadStudents}>Retry</button>
                    </div>
                  </td>
                </tr>

              ) : filteredStudents.length === 0 ? (

                <tr>
                  <td
                    colSpan="10"
                    className="dhs_students_no_data"
                  >
                    <div className="dhs_students_state">
                      <strong>No students found</strong>
                      <p>
                        {students.length === 0
                          ? "Add the first student to start building this directory."
                          : "No records match the selected filters."}
                      </p>
                      {students.length === 0 ? (
                        <button
                          onClick={() =>
                            navigate("/school-admin/students/add")
                          }
                        >
                          Add Student
                        </button>
                      ) : (
                        <button onClick={clearFilters}>Clear Filters</button>
                      )}
                    </div>
                  </td>
                </tr>

              ) : (

                filteredStudents.map((s) => (

                  <tr
                    key={s.id}
                    className="dhs_students_row"
                    onClick={() =>
                      goToDetails(s.id)
                    }
                  >

                    <td>

                      {s.photo ? (

                        <img
                          src={`https://navith.cloud${s.photo}`}
                          alt={s.name || "Student"}
                          className="dhs_students_photo"
                        />

                      ) : (

                        <div className="dhs_students_avatar">
                          {s.name?.charAt(0) || "S"}
                        </div>

                      )}

                    </td>

                    <td>
                      <div className="dhs_students_name_cell">
                        <strong>{s.name || "-"}</strong>
                        <span>{s.student_id || "No student ID"}</span>
                      </div>
                    </td>

                    <td>
                      <span className="dhs_students_id_pill">
                        {s.student_id || "-"}
                      </span>
                    </td>

                    <td>
                      {s.class || "-"}
                    </td>

                    <td>
                      {s.section || "-"}
                    </td>

                    <td>
                      {s.roll_number || "-"}
                    </td>

                    <td>
                      {s.gender || "-"}
                    </td>

                    <td>
                      {s.parent_name || "-"}
                    </td>

                    <td>
                      {s.parent_phone || "-"}
                    </td>

                    {/* ACTIONS */}

                    <td>

                      <div className="dhs_students_action_wrap">

                        {/* <button
                          className="dhs_students_archive_btn"
                          onClick={(e) => {

                            e.stopPropagation();

                            archiveStudent(s.id);

                          }}
                        >
                          Archive
                        </button> */}

                        <button
                          className="dhs_students_view_btn"
                          onClick={(e) => {

                            e.stopPropagation();

                            goToDetails(s.id);

                          }}
                        >
                          View
                        </button>

                        <button
                          className="dhs_students_delete_btn"
                          onClick={(e) => {

                            e.stopPropagation();

                            deleteStudent(s.id);

                          }}
                        >
                          Delete
                        </button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      </div>


  );
}
